import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Package, MagnifyingGlass, UserPlus } from "@phosphor-icons/react";

export default function UnclaimedPackages() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [claimTarget, setClaimTarget] = useState(null);
  const [claim, setClaim] = useState({ recipient_name: "", recipient_email: "", recipient_phone: "", recipient_address: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/packages/unclaimed", { params: { search: search || undefined } });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Only the designated holding tenant can view unclaimed packages");
      } else {
        toast.error("Failed to load unclaimed packages");
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openClaim = (pkg) => {
    setClaimTarget(pkg);
    setClaim({
      recipient_name: pkg.recipient_name || "",
      recipient_email: pkg.recipient_email || "",
      recipient_phone: pkg.recipient_phone || "",
      recipient_address: pkg.recipient_address || "",
    });
  };

  const submitClaim = async () => {
    if (!claim.recipient_name || !claim.recipient_email) {
      toast.error("Recipient name and email are required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/packages/${claimTarget.id}/claim`, claim);
      toast.success(`${claimTarget.tracking_number} claimed`);
      setClaimTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to claim package");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="unclaimed-page">
      <div className="mb-8">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Warehouse operations</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>Unclaimed Packages</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-3xl">
          Parcels received at the Miami, FL warehouse that arrived without identifiable recipient info. They&apos;re held here until a customer contacts us to claim them.
        </p>
      </div>

      <div className="flex gap-2 max-w-xl mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            data-testid="unclaimed-search"
            className="!pl-11"
            placeholder="Search tracking, sender, description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          />
        </div>
        <Button onClick={load} data-testid="unclaimed-search-btn">Search</Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="unclaimed-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Tracking</th>
              <th className="px-5 py-3 font-semibold">Sender</th>
              <th className="px-5 py-3 font-semibold">Weight</th>
              <th className="px-5 py-3 font-semibold">Description</th>
              <th className="px-5 py-3 font-semibold">Received</th>
              <th className="px-5 py-3 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-16 text-center text-zinc-500">
                <Package size={32} className="mx-auto mb-2 text-zinc-300" />
                No unclaimed packages
              </td></tr>
            ) : rows.map((p) => (
              <tr key={p.id} className="border-b border-zinc-100 last:border-0" data-testid={`unclaimed-row-${p.id}`}>
                <td className="px-5 py-3 font-mono text-zinc-900">{p.tracking_number}</td>
                <td className="px-5 py-3 text-zinc-700">{p.sender_name || "—"}</td>
                <td className="px-5 py-3 text-zinc-700 whitespace-nowrap">{p.weight_kg?.toFixed?.(2) || "0.00"} kg</td>
                <td className="px-5 py-3 text-zinc-600 text-xs max-w-md truncate" title={p.description}>{p.description || "—"}</td>
                <td className="px-5 py-3 text-xs text-zinc-500 whitespace-nowrap">
                  {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-5 py-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => openClaim(p)} data-testid={`claim-btn-${p.id}`}>
                    <UserPlus size={13} className="mr-1.5" /> Claim
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && total > 0 && (
          <div className="px-5 py-2 text-xs text-zinc-500 border-t border-zinc-100">
            {total} unclaimed package{total === 1 ? "" : "s"} held at warehouse.
          </div>
        )}
      </div>

      <Dialog open={!!claimTarget} onOpenChange={(v) => !v && setClaimTarget(null)}>
        <DialogContent data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle>Claim package {claimTarget?.tracking_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="claim-name">Recipient name *</Label>
              <Input id="claim-name" value={claim.recipient_name} onChange={(e) => setClaim({ ...claim, recipient_name: e.target.value })} data-testid="claim-name-input" />
            </div>
            <div>
              <Label htmlFor="claim-email">Recipient email *</Label>
              <Input id="claim-email" type="email" value={claim.recipient_email} onChange={(e) => setClaim({ ...claim, recipient_email: e.target.value })} data-testid="claim-email-input" />
            </div>
            <div>
              <Label htmlFor="claim-phone">Recipient phone</Label>
              <Input id="claim-phone" value={claim.recipient_phone} onChange={(e) => setClaim({ ...claim, recipient_phone: e.target.value })} data-testid="claim-phone-input" />
            </div>
            <div>
              <Label htmlFor="claim-addr">Recipient address</Label>
              <Input id="claim-addr" value={claim.recipient_address} onChange={(e) => setClaim({ ...claim, recipient_address: e.target.value })} data-testid="claim-address-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClaimTarget(null)}>Cancel</Button>
            <Button onClick={submitClaim} disabled={saving} data-testid="claim-submit-btn">
              {saving ? "Claiming…" : "Claim package"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
